/* eslint-disable camelcase */
import { errors } from '.';
import { PlanType } from '../constants/plan';
import { decamelizeKeys } from '../lib';
import { getBackVersion } from '../config';
import { getWorkspaceAliasFromUrl } from '../lib/workspace';
import {
    CardSnapshot,
    Plan,
    Subscription,
    SubscriptionSnapshot,
} from '../models';
import {
    SubscriptionTransactionDetailsSnapshot,
    SubscriptionTransactionSnapshot,
} from '../models/Subscription/SubscriptionTransaction';
import api from './api';
import { PlanResponse } from './plan';

type SubscriptionResponse = Omit<SubscriptionSnapshot, 'plan' | 'card'> & {
    plan: PlanResponse;
    card: CardSnapshot;
};

const getSubscriptions = (): Promise<SubscriptionResponse[]> => {
    if (!getWorkspaceAliasFromUrl())
        return Promise.reject(errors.WorkspaceNotFound);
    return api.request<SubscriptionResponse[]>({
        method: 'GET',
        url: `${getBackVersion()}/account/subscriptions`,
    });
};

type SubscriptionPaymentArgs =
    | {
          paymentMethod?: 'pix';
      }
    | {
          paymentMethod?: 'boleto';
      }
    | {
          cardCvv: string;
          cardId: number;
          paymentMethod: 'credit_card';
      };

export type CreateSubscriptionArgs = {
    planId: Plan['id'];
    customerId?: number;
    deduplicationId: string;
} & SubscriptionPaymentArgs;

export type CreateFreeTrialArgs = {
    planType: PlanType;
    deduplicationId: string;
};

export type UpdateSubscriptionArgs = Omit<
    CreateSubscriptionArgs,
    'deduplicationId'
> &
    SubscriptionPaymentArgs;

const serializePaymentArgs = (
    args: CreateSubscriptionArgs | UpdateSubscriptionArgs
): Record<string, unknown> => {
    const methodArgs =
        args.paymentMethod === 'credit_card'
            ? {
                  card_id: args.cardId,
                  card_cvv: args.cardCvv,
              }
            : {};
    return {
        payment_method: args.paymentMethod,
        customer_id: args.customerId,
        plan_id: args.planId,
        ...('deduplicationId' in args
            ? { deduplication_id: args.deduplicationId }
            : {}),
        ...methodArgs,
    };
};

const createSubscription = (
    args: CreateSubscriptionArgs
): Promise<SubscriptionResponse> => {
    return api.request<SubscriptionResponse>({
        method: 'POST',
        url: `${getBackVersion()}/account/subscriptions`,
        data: serializePaymentArgs(args),
    });
};

const createFreeTrial = (
    args: CreateFreeTrialArgs
): Promise<SubscriptionResponse> => {
    return api.request<SubscriptionResponse>({
        method: 'POST',
        url: `${getBackVersion()}/account/subscriptions/free_trial`,
        data: decamelizeKeys(args),
    });
};

const getSubscription = (
    id: SubscriptionSnapshot['id']
): Promise<SubscriptionResponse> =>
    api.request<SubscriptionResponse>({
        method: 'GET',
        url: `${getBackVersion()}/account/subscriptions/` + id,
    });

const updateSubscription = (
    subscriptionId: Subscription['id'],
    args: UpdateSubscriptionArgs
): Promise<SubscriptionResponse> => {
    return api.request<SubscriptionResponse>({
        method: 'PUT',
        url: `${getBackVersion()}/account/subscriptions/` + subscriptionId,
        data: serializePaymentArgs(args),
    });
};

const getSubscriptionTransactionDetails = (
    subscriptionId: SubscriptionSnapshot['id'],
    transactionId: SubscriptionTransactionSnapshot['id']
): Promise<SubscriptionTransactionDetailsSnapshot[]> => {
    return api.request<SubscriptionTransactionDetailsSnapshot[]>({
        method: 'GET',
        url:
            `${getBackVersion()}/account/subscriptions/${subscriptionId}/` +
            `transactions/${transactionId}`,
    });
};

const uncancelSubscription = (
    subscriptionId: Subscription['id']
): Promise<SubscriptionResponse> => {
    return api.request<SubscriptionResponse>({
        method: 'POST',
        url:
            `${getBackVersion()}/account/subscriptions/` +
            subscriptionId +
            '/uncancel',
    });
};

export default {
    createFreeTrial,
    createSubscription,
    getSubscription,
    getSubscriptions,
    getSubscriptionTransactionDetails,
    uncancelSubscription,
    updateSubscription,
};
export type { SubscriptionResponse };
